import type { MetricValues, SecondaryDimensionValues } from '@revelio/core';

import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import {
  COLUMN_LEVEL_LABELS,
  type ColumnDimension,
  isColumnLevel,
} from '../../PageConfigProvider/types/dimension';
import type { TableColumnConfig } from '../../PageConfigProvider/types/item';

export const getColumnDimensionLabel = (dimension: ColumnDimension): string => {
  if (isColumnLevel(dimension)) return COLUMN_LEVEL_LABELS[dimension];
  switch (dimension) {
    case 'company':
      return 'Company';
    case 'geography':
      return 'Geography';
    case 'role':
      return 'Role';
    case 'rics':
      return 'Industry';
    case 'skills':
      return 'Skills';
    case 'activities':
      return 'Activities';
    case 'seniority':
      return 'Seniority';
    case 'education':
      return 'Education';
    case 'ethnicity':
      return 'Ethnicity';
    case 'gender':
      return 'Gender';
    case 'competitors':
      return 'Competitors';
    case 'subsidiaries':
      return 'Subsidiaries';
    case 'timeseries':
      return 'Time';
  }
};

/**
 * The breakdown dimension that stands for the page's own entities in a table.
 * Activity has none: composition never breaks down by activity, so an activity
 * page has no primary column and callers start from the other dimensions.
 */
export const getPrimaryDimension = (
  primaryFilterId: PrimaryFilterId
): SecondaryDimensionValues | undefined => {
  switch (primaryFilterId) {
    case FilterId.COMPANY:
      return 'company';
    case FilterId.INDUSTRY:
      return 'rics';
    case FilterId.GEOGRAPHY:
      return 'geography';
    case FilterId.ROLE:
      return 'role';
    case FilterId.CUSTOM_SKILL:
      return 'skills';
    case FilterId.ACTIVITY:
      return undefined;
  }
};

export const buildTableColumns = ({
  primaryFilterId,
  secondaryDimension,
  metrics,
}: {
  primaryFilterId: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metrics: readonly MetricValues[];
}): TableColumnConfig[] => {
  const primaryDimension = getPrimaryDimension(primaryFilterId);
  const dimensions = [primaryDimension, secondaryDimension].filter(
    (dimension): dimension is SecondaryDimensionValues =>
      dimension !== undefined
  );
  return [
    ...dimensions.map(
      (dimension): TableColumnConfig => ({
        id: crypto.randomUUID(),
        kind: 'dimension',
        dimension,
        alignment: 'left',
      })
    ),
    ...metrics.map(
      (metric): TableColumnConfig => ({
        id: crypto.randomUUID(),
        kind: 'variable',
        metric,
        aggregation: 'sum',
      })
    ),
  ];
};
